"use client";

import Surface from "@/components/_shared/controls/Surface";
import { QueryKeys } from "@/data/globals";
import { CmsContentItem, CmsSlotType } from "@/data/types";
import { CmsContentSlot } from "@/prisma/generated/browser";
import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import CmsContentListEditor from "./CmsContentListEditor";
import CmsSlotParametersEditor from "./CmsSlotParametersEditor";

type Props = {
  selectedSlot: CmsContentSlot | null;
  inlineEditor?: boolean;
};

const getNewSlot = (): CmsContentSlot => ({
  id: nanoid(12),
  key: "",
  content: null,
  validFrom: null,
  validTo: null,
  type: CmsSlotType.Banner,
  parameters: {}
});

export default function CmsContentSlotEditorView({ selectedSlot, inlineEditor }: Props) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [slot, setSlot] = useState<CmsContentSlot>(getNewSlot());
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (selectedSlot) {
      setSlot(selectedSlot);
    } else {
      setSlot(getNewSlot());
    }
    setDirty(false);
  }, [selectedSlot]);

  //#region Save

  const saveMutation = useMutation({
    mutationFn: async (data: CmsContentSlot) => {
      const response = await fetch(`/api/admin/cms/content-slots/${data.id}`, {
        method: "POST",
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error("Failed to save");
      return response.json() as Promise<CmsContentSlot>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CMS_SLOTS] });
      queryClient.setQueryData([QueryKeys.CMS_SLOTS, data.id], data);
      setDirty(false);
      if (inlineEditor) router.refresh();
    }
  });

  const updateSlot = async () => {
    if (!slot.key.trim()) {
      alert("Slot key is required");
      return;
    }

    try {
      await saveMutation.mutateAsync(slot);
    } catch (error) {
      console.error("Error saving slot:", error);
      alert("Failed to save slot");
    }
  };

  //#endregion

  //#region Delete

  const deleteMutation = useMutation({
    mutationFn: async (slotId: string) => {
      const response = await fetch(`/api/admin/cms/content-slots/${slotId}`, {
        method: "DELETE"
      });
      if (!response.ok) throw new Error("Failed to delete");
      return response.json();
    },
    // oxlint-disable-next-line no-unused-vars
    onSuccess: (_, slotId) => {
      queryClient.invalidateQueries({ queryKey: ["cmsContentSlots"] });
      setSlot(getNewSlot());
      setDirty(false);
    }
  });

  const deleteSlot = async () => {
    if (!slot.id) return;
    if (!confirm(`Are you sure you want to delete ${slot.key}?`)) return;

    try {
      await deleteMutation.mutateAsync(slot.id);
    } catch (error) {
      console.error("Error deleting slot:", error);
      alert("Failed to delete slot");
    }
  };

  //#endregion

  const saving = saveMutation.isPending || deleteMutation.isPending;

  const contentItems: CmsContentItem[] = slot.content
    ? Array.isArray(slot.content)
      ? (slot.content as any as CmsContentItem[])
      : []
    : [];

  return (
    <Surface>
      <Stack direction="row" sx={{ p: 2 }} spacing={2}>
        <TextField
          size="small"
          label="Slot Key"
          value={slot.key}
          onChange={(e) => {
            setSlot((x) => ({ ...x, key: e.target.value }));
            setDirty(true);
          }}
          sx={{ flex: 2 }}
          disabled={saving || inlineEditor}
        />
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Slot Type</InputLabel>
          <Select
            label="Slot Type"
            title="Slot Type"
            size="small"
            value={slot.type}
            onChange={(e) => {
              setSlot((x) => ({
                ...x,
                type: parseInt(e.target.value as any) as CmsSlotType
              }));
              setDirty(true);
            }}
            disabled={saving}
          >
            <MenuItem value={CmsSlotType.Banner}>Banner</MenuItem>
            <MenuItem value={CmsSlotType.Grid}>Grid</MenuItem>
            <MenuItem value={CmsSlotType.Page}>Page</MenuItem>
            <MenuItem value={CmsSlotType.Stack}>Stack</MenuItem>
          </Select>
        </FormControl>
        {saving ? (
          <CircularProgress size={32} sx={{ alignSelf: "center" }} />
        ) : (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => updateSlot()} disabled={!dirty} sx={{ ml: 2 }}>
              Save
            </Button>
            {slot.key && (
              <Button variant="outlined" color="error" onClick={() => deleteSlot()}>
                Delete
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {/* Content section */}
      <Divider sx={{ my: 1 }} />
      <CmsContentListEditor
        slotType={slot.type}
        items={contentItems}
        setSlotContent={(updatedContent) => {
          setSlot((x) => ({
            ...x,
            content: updatedContent as any
          }));
          setDirty(true);
        }}
      />

      {/* Validity dates */}
      <Divider sx={{ my: 1 }} />

      <CmsSlotParametersEditor
        type={slot.type}
        parameters={slot.parameters as any}
        setSlotParameters={(parameters) => {
          setDirty(true);
          setSlot((x) => ({ ...x, parameters }));
        }}
      />

      <Stack direction="row" spacing={2} sx={{ p: 2 }}>
        <TextField
          label="Valid From"
          type="datetime-local"
          size="small"
          value={slot.validFrom ? new Date(slot.validFrom).toISOString().slice(0, 16) : ""}
          onChange={(e) => {
            setSlot((x) => ({
              ...x,
              validFrom: e.target.value ? new Date(e.target.value) : null
            }));
            setDirty(true);
          }}
          sx={{ flex: 1 }}
          disabled={saving}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Valid To"
          type="datetime-local"
          size="small"
          value={slot.validTo ? new Date(slot.validTo).toISOString().slice(0, 16) : ""}
          onChange={(e) => {
            setSlot((x) => ({
              ...x,
              validTo: e.target.value ? new Date(e.target.value) : null
            }));
            setDirty(true);
          }}
          sx={{ flex: 1 }}
          slotProps={{ inputLabel: { shrink: true } }}
          disabled={saving}
        />
      </Stack>

      {/* <LocalDebugDisplay obj={slot} /> */}
    </Surface>
  );
}
